import React from 'react';
import PropTypes from 'prop-types';
import { Checkbox } from '@cimpress/react-components';
import { countBy, some } from 'lodash';
import { injectIntl, createIntl, createIntlCache } from 'react-intl';

// You can pass your messages to the IntlProvider. Optional: remove if unneeded.
const messages = require('../../i18n').default['en']; // en.json

// This is optional but highly recommended
// since it prevents memory leak
const cache = createIntlCache();

const intl = createIntl(
  {
    locale: 'en',
    messages,
  },
  cache
);

const productionStatus = {
  value: 'inProduction',
  label: intl.formatMessage({ id: 'Statuses.InProduction' }),
};
const productionSubStatuses = [
  {
    value: 'production.production',
    label: intl.formatMessage({ id: 'Statuses.Production' }),
  },
  {
    value: 'production.productionAccepted',
    label: intl.formatMessage({ id: 'Statuses.ProductionAccepted' }),
  },
];

const onHoldStatus = {
  value: 'onHold',
  label: intl.formatMessage({ id: 'Statuses.OnHold' }),
};
const onHoldSubStatuses = [
  {
    value: 'onHold.artworkHold',
    label: intl.formatMessage({ id: 'Statuses.ArtworkHold' }),
  },
  {
    value: 'onHold.routingHold',
    label: intl.formatMessage({ id: 'Statuses.RoutingHold' }),
  },
];

// ECOM-3216 Item Sub-filters
export function selectionChange(item, selectedItemsKey, props) {
  const { selectedItems, setProductionIndeterminate } = props;

  /** @type {Array} */
  let selectedState = [...selectedItems];

  /**
   * Is this item Production?
   *  - Yes
   *    Is Production already in the List?
   *      + Yes
   *        Remove Production
   *        Remove Substatuses
   *        Set productionIndeterminate to false
   *      + No
   *        Add Production
   *        Add Substatuses
   *        Set productionIndeterminate to false
   *  - No, Is this Item production.* ?
   *    + Yes
   *      Is this Item already in the List?
   *      - Yes
   *        Remove Item
   *      - No
   *        Add Item
   *      How many substatuses are left?
   *      + 0
   *        Set productionIndeterminate to false
   *        break;
   *      + possibleSubStatuses.length
   *        Add Production
   *        Set productionIndeterminate to false
   *        break;
   *      + default:
   *        Remove Production
   *        Set productionIndeterminate to true
   *    + No
   *        Add Status
   *        Add Production (deduplicated later)
   *  - No, It's some other status
   *    call props.selectionChange as usual
   */
  function indexOfItemInSelectedState(item) {
    return selectedState.map(s => s.value).indexOf(item.value);
  }
  function isItemProduction() {
    return item.value === productionStatus.value;
  }
  function isItemSubStatus() {
    return String(item.value).startsWith('production.');
  }
  function isProductionSelected() {
    return selectedState.some(s => s.value === productionStatus.value);
  }
  function isItemAlreadySelected() {
    return selectedState.map(s => s.value).indexOf(item.value) !== -1;
  }
  function removeItemFromSelectedState() {
    selectedState.splice(indexOfItemInSelectedState(item), 1);
  }
  function removeAllSubStatusesFromSelectedState() {
    let selectedStateValues = selectedState.map(s => s.value);
    for (let status of productionSubStatuses) {
      if (selectedStateValues.includes(status.value)) {
        let index = selectedState.map(s => s.value).indexOf(status.value);
        selectedState.splice(index, 1);
      }
      // if it's not in the array, skip removal
    }
  }
  function removeProductionFromSelectedItems() {
    if (indexOfItemInSelectedState(productionStatus) !== -1) {
      selectedState.splice(indexOfItemInSelectedState(productionStatus), 1);
    }
  }
  function isItemOnHold() {
    return item.value === onHoldStatus.value;
  }
  function isItemOnHoldSubStatus() {
    return String(item.value).startsWith('onHold.');
  }
  function isOnHoldSelected() {
    return selectedState.some(s => s.value === onHoldStatus.value);
  }
  function removeAllOnHoldSubStatusesFromSelectedState() {
    let selectedStateValues = selectedState.map(s => s.value);
    for (let status of onHoldSubStatuses) {
      if (selectedStateValues.includes(status.value)) {
        let index = selectedState.map(s => s.value).indexOf(status.value);
        selectedState.splice(index, 1);
      }
    }
  }
  function removeOnHoldFromSelectedItems() {
    if (indexOfItemInSelectedState(onHoldStatus) !== -1) {
      selectedState.splice(indexOfItemInSelectedState(onHoldStatus), 1);
    }
  }

  if (isItemProduction()) {
    if (isProductionSelected()) {
      selectedState.splice(indexOfItemInSelectedState(productionStatus), 1); // remove production
      removeAllSubStatusesFromSelectedState(); // remove substatuses
    } else {
      selectedState.push(productionStatus); // add production
      selectedState = selectedState.concat(productionSubStatuses); // add substatus
    }
    setProductionIndeterminate(false); // both cases
  } else if (isItemSubStatus()) {
    // Add or Remove Item Accordingly
    if (isItemAlreadySelected()) {
      removeItemFromSelectedState();
    } else {
      selectedState.push(item);
    }
    // With Item added or Removed, get count of substatuses for determining indeterminacy
    switch (countBy(selectedState, item => String(item.value).startsWith('production.')).true) {
      case 0:
      case undefined: {
        // countBy returns an object, the `true` property may be undefined
        setProductionIndeterminate(false);
        break;
      }
      case productionSubStatuses.length: {
        selectedState.push(productionStatus);
        setProductionIndeterminate(false);
        break;
      }
      default: {
        setProductionIndeterminate(true);
        removeProductionFromSelectedItems();
        break;
      }
    }
  } else if (isItemOnHold()) {
    const { setOnHoldIndeterminate } = props;
    if (isOnHoldSelected()) {
      selectedState.splice(indexOfItemInSelectedState(onHoldStatus), 1); // remove onHold
      removeAllOnHoldSubStatusesFromSelectedState(); // remove substatuses
    } else {
      selectedState.push(onHoldStatus); // add onHold
      selectedState = selectedState.concat(onHoldSubStatuses); // add substatuses
    }
    setOnHoldIndeterminate(false); // both cases
  } else if (isItemOnHoldSubStatus()) {
    const { setOnHoldIndeterminate } = props;
    // Add or Remove Item Accordingly
    if (isItemAlreadySelected()) {
      removeItemFromSelectedState();
    } else {
      selectedState.push(item);
    }
    // With Item added or Removed, get count of substatuses for determining indeterminacy
    switch (countBy(selectedState, item => String(item.value).startsWith('onHold.')).true) {
      case 0:
      case undefined: {
        setOnHoldIndeterminate(false);
        break;
      }
      case onHoldSubStatuses.length: {
        selectedState.push(onHoldStatus);
        setOnHoldIndeterminate(false);
        break;
      }
      default: {
        setOnHoldIndeterminate(true);
        removeOnHoldFromSelectedItems();
        break;
      }
    }
  } else {
    // This item isn't the production status or a substatus, proceed as usual
    props.selectionChange(item, selectedItemsKey);
    return;
  }

  // Deduplicate List to avoid issues
  selectedState = Array.from(new Set(selectedState.map(x => x.value))).map(v => selectedState.find(i => i.value === v));

  props.selectionChange(selectedState, selectedItemsKey);
}

const ItemCheckbox = props => {
  const { item, selectedItems, selectedItemsKey } = props;

  // add production to selected, if for some reason all substatuses are selected and it isn't
  // prevents page-load bug where first click on Production (3) (when already selected) will Add the selection
  // behind the scenes. This causes UX Bug, where first click is "eaten" and appears to have no action
  if (
    countBy(selectedItems, item => String(item.value).startsWith('production.')).true === productionSubStatuses.length
  ) {
    if (!selectedItems.some(si => si.value === productionStatus.value)) {
      selectedItems.push(productionStatus);
    }
  }

  // same page-load fix for onHold sub-statuses
  if (countBy(selectedItems, item => String(item.value).startsWith('onHold.')).true === onHoldSubStatuses.length) {
    if (!selectedItems.some(si => si.value === onHoldStatus.value)) {
      selectedItems.push(onHoldStatus);
    }
  }

  // ECOM-3216 Item Sub-statuses
  const isProductionChecked = () => {
    if (item.value === 'inProduction') {
      if (
        countBy(selectedItems, item => String(item.value).startsWith('production.')).true ===
        productionSubStatuses.length
      ) {
        return true;
      }
    }
    return false;
  };

  const isOnHoldChecked = () => {
    if (item.value === 'onHold') {
      if (countBy(selectedItems, item => String(item.value).startsWith('onHold.')).true === onHoldSubStatuses.length) {
        return true;
      }
    }
    return false;
  };

  return (
    <Checkbox
      style={{ padding: '6px 16px' }}
      label={item.label}
      checked={
        isProductionChecked() ||
        isOnHoldChecked() ||
        some(selectedItems, selectedItem => selectedItem.value === item.value)
      }
      onChange={() => selectionChange(item, selectedItemsKey, props)}
      indeterminate={
        (item.value === 'inProduction' &&
          countBy(selectedItems, item => String(item.value).startsWith('production.')).true > 0 &&
          countBy(selectedItems, item => String(item.value).startsWith('production.')).true < 3) ||
        (item.value === 'onHold' &&
          countBy(selectedItems, item => String(item.value).startsWith('onHold.')).true > 0 &&
          countBy(selectedItems, item => String(item.value).startsWith('onHold.')).true < onHoldSubStatuses.length + 1)
      }
    />
  );
};

ItemCheckbox.propTypes = {
  item: PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  }),
  selectedItems: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  selectedItemsKey: PropTypes.string,
  selectionChange: PropTypes.func,
};

export default injectIntl(ItemCheckbox);
